import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Button } from '../components/ui/button';
import { jobAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  Clock, CheckCircle2, XCircle, Loader2, Ban, RefreshCw, AlertCircle
} from 'lucide-react';

const statusIcons = {
  pending: <Clock className="w-5 h-5 text-amber-500" />,
  queued: <Clock className="w-5 h-5 text-amber-500" />,
  running: <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />,
  succeeded: <CheckCircle2 className="w-5 h-5 text-emerald-500" />,
  failed: <XCircle className="w-5 h-5 text-red-500" />,
  cancelled: <Ban className="w-5 h-5 text-zinc-500" />,
};

const statusColors = {
  pending: 'warning',
  queued: 'warning',
  running: 'info',
  succeeded: 'success',
  failed: 'destructive',
  cancelled: 'secondary',
};

const levelColors = {
  info: 'text-blue-600 bg-blue-50',
  warning: 'text-amber-600 bg-amber-50',
  error: 'text-red-600 bg-red-50',
};

export default function JobDetailDialog({ jobId, open, onOpenChange, onStatusChange }) {
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (open && jobId) {
      loadJobDetail();
    }
  }, [open, jobId]);

  // Auto-refresh for running jobs
  useEffect(() => {
    if (!open || !job) return;
    if (!['pending', 'validating_files', 'queued', 'running'].includes(job.status)) return;

    const interval = setInterval(() => {
      loadJobDetail(true);
    }, 5000);

    return () => clearInterval(interval);
  }, [open, job?.status]);

  const loadJobDetail = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);

    try {
      const res = await jobAPI.getDetail(jobId);
      setJob(res.data);

      // Notify parent if status changed
      if (onStatusChange && res.data.status !== job?.status) {
        onStatusChange(res.data);
      }
    } catch (error) {
      if (!silent) toast.error('Failed to load job details');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleString();
  };

  const getProgressColor = () => {
    if (job?.status === 'succeeded') return 'bg-emerald-500';
    if (job?.status === 'failed') return 'bg-red-500';
    if (job?.status === 'cancelled') return 'bg-zinc-500';
    return 'bg-blue-500';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-3">
              {job && statusIcons[job.status]}
              Fine-tuning Job Details
            </DialogTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => loadJobDetail()}
              disabled={refreshing}
              data-testid="refresh-job-detail-btn"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          <DialogDescription>
            {job?.name || job?.base_model || 'Job details and training logs'}
          </DialogDescription>
        </DialogHeader>

        {loading && !job ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : job ? (
          <div className="flex-1 overflow-y-auto space-y-6">
            {/* Progress Bar */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Progress</span>
                <span className="font-medium">{job.progress || 0}%</span>
              </div>
              <div className="relative h-3 bg-secondary rounded-full overflow-hidden">
                <div
                  className={`absolute inset-y-0 left-0 transition-all duration-500 rounded-full ${getProgressColor()}`}
                  style={{ width: `${job.progress || 0}%` }}
                />
              </div>
            </div>

            {/* Status Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-muted/50 rounded-lg">
                <div className="text-xs text-muted-foreground mb-1">Status</div>
                <Badge variant={statusColors[job.status]}>{job.status}</Badge>
              </div>
              <div className="p-3 bg-muted/50 rounded-lg">
                <div className="text-xs text-muted-foreground mb-1">Vendor</div>
                <span className="font-medium capitalize">{job.vendor_code}</span>
              </div>
              <div className="p-3 bg-muted/50 rounded-lg">
                <div className="text-xs text-muted-foreground mb-1">Base Model</div>
                <span className="font-mono text-sm">{job.base_model}</span>
              </div>
              <div className="p-3 bg-muted/50 rounded-lg">
                <div className="text-xs text-muted-foreground mb-1">Epochs</div>
                <span className="font-medium">{job.n_epochs}</span>
              </div>
            </div>

            {/* Fine-tuned Model */}
            {job.fine_tuned_model && (
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div className="text-xs text-emerald-600 mb-1">Fine-tuned Model ID</div>
                <code className="text-sm font-mono">{job.fine_tuned_model}</code>
              </div>
            )}

            {/* Error Message */}
            {job.error_message && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2 text-red-600 mb-1">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-xs font-medium">Error</span>
                </div>
                <p className="text-sm text-red-700">{job.error_message}</p>
              </div>
            )}

            {/* Timestamps */}
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-xs text-muted-foreground mb-1">Created</div>
                <span>{formatTime(job.created_at)}</span>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1">Started</div>
                <span>{formatTime(job.started_at)}</span>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1">Finished</div>
                <span>{formatTime(job.finished_at)}</span>
              </div>
            </div>

            {/* Training Logs */}
            <div>
              <h4 className="text-sm font-medium mb-3">Training Logs</h4>
              <div className="bg-zinc-900 rounded-lg p-4 max-h-64 overflow-y-auto">
                {job.events && job.events.length > 0 ? (
                  <div className="space-y-2">
                    {job.events.map((event, idx) => (
                      <div
                        key={event.id || idx}
                        className={`text-xs font-mono p-2 rounded ${levelColors[event.level] || 'text-zinc-300 bg-zinc-800'}`}
                        data-testid={`log-entry-${idx}`}
                      >
                        <span className="opacity-60">[{formatTime(event.created_at)}]</span>{' '}
                        <span className="uppercase font-bold">{event.level}</span>:{' '}
                        {event.message}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-zinc-500 text-sm text-center py-4">
                    {['pending', 'queued'].includes(job.status) ? (
                      'Waiting for job to start...'
                    ) : job.status === 'running' ? (
                      'Fetching logs from vendor...'
                    ) : (
                      'No logs available'
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Trained Tokens */}
            {job.trained_tokens && (
              <div className="text-sm text-muted-foreground">
                Trained tokens: <span className="font-medium">{job.trained_tokens.toLocaleString()}</span>
              </div>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
